import { useCallback, useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { type Row } from '@tanstack/react-table';
import { type SelectionEventProps } from '@/components/layouts/gallery-layout';
import { useGallerySelection } from '@/context/GallerySelectionContext';
import { useBrowserStore } from '@/hooks/data/browser/useBrowserStore';
import { isAsset, isSelectableAsset } from '@/lib/utils';
import { useTreeContext } from '@/context/TreeContext';
import { DAMNodeType, NodeType } from '@/types/tree';
import { type Asset } from '@/types/asset';

export const useTableSelection = ({ items }: { items: Array<Asset> }) => {
  const setIsAllSelected = useBrowserStore(useShallow((state) => state.setIsAllSelected));
  const { selectedAssets } = useGallerySelection();
  const { isAllSelected } = useBrowserStore(useShallow((s) => ({ isAllSelected: s.isAllSelected })));
  const { toggleItemSelection } = useGallerySelection();
  const { setCurrentSelectedType, toggleNodeSelection } = useTreeContext();

  const toggleItemSelectionRef = useRef(toggleItemSelection);
  const toggleNodeSelectionRef = useRef(toggleNodeSelection);

  useEffect(() => {
    toggleItemSelectionRef.current = toggleItemSelection;
    toggleNodeSelectionRef.current = toggleNodeSelection;
  }, [toggleItemSelection, toggleNodeSelection]);

  // The pool every range and select-all walks. Rows a live broadcast has taken out are
  // filtered here rather than at each call site, so shift-ranges and keyboard shortcuts
  // cannot sweep up an asset that has left the list or stopped existing.
  const itemsRef = useRef<Array<Asset>>(items.filter(isSelectableAsset));
  useEffect(() => {
    itemsRef.current = items.filter(isSelectableAsset);
  }, [items]);

  // Keep latest selection available without forcing handleSelection to re-create every click
  const selectedItemsRef = useRef<Array<Asset>>(selectedAssets);
  useEffect(() => {
    selectedItemsRef.current = selectedAssets;
  }, [selectedAssets]);

  // Same pattern for "select all" to avoid stale closures in a stable callback
  const isAllSelectedRef = useRef(isAllSelected);
  useEffect(() => {
    isAllSelectedRef.current = isAllSelected;
  }, [isAllSelected]);

  // Stable callback identity prevents downstream table/column re-initialization on selection changes
  const handleSelection = useCallback(
    <TData extends Asset>(row: Row<TData>, event?: SelectionEventProps) => {
      const selectedItemsSnapshot = selectedItemsRef.current;
      let keys = {};
      if (event) {
        keys = {
          metaKey: event.metaKey,
          shiftKey: event.shiftKey,
          ctrlKey: event.ctrlKey,
        };
      }

      // Disable de-selecting an asset by clicking on it directly
      if (
        selectedItemsSnapshot.length === 1 &&
        selectedItemsSnapshot[0].id === row.original.id &&
        Object.values(keys).every((key) => !key)
      ) {
        return;
      }

      const isAnAsset = isAsset(row.original.type) || row.original.type === null || row.original.type === undefined;

      /**
       * Not `row.index`: it counts every table row, while the pool is filtered to
       * selectable ones, so a table index sweeps extra rows into any shift-range that
       * spans a trashed/deleted row. -1 = the clicked row itself is not selectable.
       */
      const poolIndex = itemsRef.current.findIndex((item) => item.id === row.original.id);
      if (poolIndex === -1) {
        return;
      }

      if (isAllSelectedRef.current) {
        setIsAllSelected(false);
      }

      const newAssets = toggleItemSelectionRef.current({
        asset: row.original,
        assets: itemsRef.current,
        index: poolIndex,
        ...keys,
      });

      // TEMPORARY DOUBLE-WRITE of the older TreeContext.selectedNodes store alongside the
      // gallery selection above — a follow-up PR untangles the two. See TreeContext.selectedNodes.
      toggleNodeSelectionRef.current({
        asset: {
          ...row.original,
          id: row.original.nodeId,
          type: isAnAsset ? DAMNodeType.Asset : (row.original.type as DAMNodeType),
        },
        assets: itemsRef.current,
        index: poolIndex,
        ...keys,
      });

      // TODO: Clean this up
      // trash page data object has types set as 'asset', 'folder', 'album',
      // but data in other gallery views has types set as 'image' and 'video'
      if (newAssets.length === 1) {
        const selectedAsset = newAssets[0];
        setCurrentSelectedType(isAsset(selectedAsset.type) ? NodeType.Assets : (`${selectedAsset.type}s` as NodeType));
      } else {
        setCurrentSelectedType(undefined);
      }
    },
    [setCurrentSelectedType, setIsAllSelected],
  );

  return {
    handleSelection,
  };
};
